import Config from '$root/Config';
import _isString from 'lodash/isString';
import setMinutes from 'date-fns/set_minutes';
import setHours from 'date-fns/set_hours';
import setSeconds from 'date-fns/set_seconds';
import subDays from 'date-fns/sub_days';
import addDays from 'date-fns/add_days';
import getTimeInMs from 'date-fns/get_time';

import { formatDate } from '$utils/Utils.js';

const formatDateForUi = (value) => (value ? formatDate(value, Config.dateFormat) : undefined);

const formatDateRangeForUi = ([from, to]) => `${formatDateForUi(from)} - ${formatDateForUi(to)}`;

const formatDateFromUi = (value, checkType = false) => {
	if (checkType && _isString(value)) {
		return getTime(value);
	}

	if (!checkType) {
		return getTime(value);
	}
};

const unixToUi = (value, type = 'seconds', dateFormat = Config.dateFormat) => {
	if(!value || isNaN(value)) return null;
	const date = new Date(0);
	const typeMap = {
		seconds: () => date.setUTCSeconds(value),
	};

	return value ? formatDate(typeMap[type](), dateFormat) : undefined
}

const getDateFromURLRange = (range) => {
	if (range && typeof range === 'object' && !Array.isArray(range)) {
		return [Number(range.min) * 1000, Number(range.max) * 1000];
	}

	return String(range).split(',').map((item) => Number(item) * 1000);
};

const getApiDateRange = ([from, to]) => ({
	min: formatTimestampToAPI(from),
	max: formatTimestampToAPI(to),
});

// A stored/API range ({ min, max } in seconds) is only usable with two real timestamps.
// Partial ranges used to be saved as NaN, which the persisted store turns into null/0.
const isValidApiDateRange = (range) => {
	if (!range || typeof range !== 'object' || Array.isArray(range)) return false;
	const min = Number(range.min);
	const max = Number(range.max);
	return Number.isFinite(min) && Number.isFinite(max) && min > 0 && max >= min;
};

const toApiDateRange = (range) => {
	if (!range || range === 'all' || (typeof range === 'object' && !Array.isArray(range))) {
		return range;
	}

	if (Array.isArray(range)) {
		return getApiDateRange(range);
	}

	if (typeof range === 'string' && range.includes(',')) {
		const [min, max] = range.split(',');
		return { min: Number(min), max: Number(max) };
	}

	return range;
};

const getTime = (value) => Math.round(new Date(value).getTime());

const getTimestampRange = ([from, to]) => `${formatTimestampToAPI(from)},${formatTimestampToAPI(to)}`;

const getEndOfDate = (date) => setSeconds(setMinutes(setHours(date, 23), 59), 59);

const getBeginningOfDate = (date) => setSeconds(setMinutes(setHours(date, 0), 0), 0);

const getRelativeTimeStampFromNow = (days = 0, end = true) => {
	const today = end ? getEndOfDate(new Date()) : getBeginningOfDate(new Date());

	let finalDate = today;

	if (days < 0) {
		finalDate = subDays(today, Math.abs(days));
	} else if (days > 0) {
		finalDate = addDays(today, days);
	}

	return getTimeInMs(finalDate);
};

const dateStrToInt = (str, getTime = true, endDate) => {
	const [day, month, year] = str.split('-');
	const date = new Date(year, Number(month) - 1, day, endDate ? 23 : 0, endDate ? 59 : 0, endDate ? 59 : 0);
	return getTime ? date.getTime() : date;
};

const formatTimestampToAPI = (value) => Math.floor(value / 1000);

const formatDateToAPITimestamp = (date) => formatTimestampToAPI(getTimeInMs(date));

const formatDateToRFC3339 = (d) => {
	const pad = (n) => n < 10 ? "0" + n : n;

	const timezoneOffset = (offset) => {
		let sign;

		if (offset === 0) {
			return "Z";
		}

		sign = (offset > 0) ? "-" : "+";
		offset = Math.abs(offset);

		return sign + pad(Math.floor(offset / 60)) + ":" + pad(offset % 60);
	}

	return d.getFullYear() + "-" +
		pad(d.getMonth() + 1) + "-" +
		pad(d.getDate()) + "T" +
		pad(d.getHours()) + ":" +
		pad(d.getMinutes()) + ":" +
		pad(d.getSeconds()) +
		timezoneOffset(d.getTimezoneOffset());
}

const minutesToHours = d => {
	d = Number(d);
    const h = Math.floor(d / 3600);
    const m = Math.floor(d % 3600 / 60);
    const s = Math.floor(d % 3600 % 60);

	const hDisplay = h > 9 ? `${h}` : `0${h}`;
	const mDisplay = m > 9 ? `${m}` : `0${m}`;
	const sDisplay = s > 9 ? `${s}` : `0${s}`;

    return `${hDisplay}:${mDisplay}:${sDisplay}`; 
};

export {
	unixToUi,
	formatTimestampToAPI,
	formatDateForUi,
	formatDateRangeForUi,
	formatDateFromUi,
	formatDateToAPITimestamp,
	formatDateToRFC3339,
	getDateFromURLRange,
	getApiDateRange,
	toApiDateRange,
	isValidApiDateRange,
	getTimestampRange,
	getBeginningOfDate,
	getEndOfDate,
	getRelativeTimeStampFromNow,
	dateStrToInt,
	minutesToHours
};
